import { Permissions, type PermissionCode } from '@/models/permission';

/**
 * What each page requires, in one place: the sidebar entries
 * (`route.tsx`, as `DashboardItem.permissions`) and the route gates
 * (`requirePermissions` / `firstAllowed`) both read from here, so a nav entry and its
 * route can never gate on different codes.
 *
 * Arrays are `oneOf` — holding any one of the codes opens the page, which is what the
 * sidebar has always meant. A page needing every code spells out `allOf` at its route.
 */
export const PagePermissions = {
  players: [Permissions.players.read],
  catalogItems: [Permissions.items.read],
  // The Bundles page is the items list filtered to type=bundle, so it reads the
  // items endpoint. `bundles:read` is reserved (see permission.ts) and governs no
  // endpoint — gating on it would 403 holders of items:read on data the backend
  // serves. Point this at Permissions.bundles.read once a bundle endpoint exists.
  catalogBundles: [Permissions.items.read],
  catalogCurrency: [Permissions.items.read],
  seasonPasses: [Permissions.seasonpass.read],
  gifts: [Permissions.gifts.read],
  // Not translations:read — no endpoint enforces it (see permission.ts). The page's
  // blocking loader fetches GET /v1/languages, which needs languages:read, and every
  // Role holding it also holds the items:read the translated item list needs.
  translations: [Permissions.languages.read],
  transactionsStore: [Permissions.store.read],
  transactionsInventory: [Permissions.inventory.read],
  snapshots: [Permissions.store.read],
  // Its sections: Naming writes the Sandbox, Supported Languages writes the language
  // list, and Export only reads. Holding any one of the three is enough to open the
  // page — sandboxes:read on purpose, since it is the one page a Sandbox reader can
  // use, and the index lands them here instead of bouncing back to the Sandbox list.
  settingsGeneral: [
    Permissions.sandboxes.read,
    Permissions.sandboxes.edit,
    Permissions.languages.edit,
  ],
  // The dedicated import codes, not create: bulk import is its own action
  // (BR-RBAC-36), and each wizard route below re-gates on its own resource.
  settingsImport: [
    Permissions.items.import,
    Permissions.gifts.import,
    Permissions.seasonpass.import,
    Permissions.store.import,
  ],
} satisfies Record<string, PermissionCode[]>;

// The same rules keyed by the path under /game/:gameCode/:sandboxCode. Longest prefix
// first, so 'items/bundle' is not answered by 'items'. Kept next to the rules above so
// a new gated page shows both entries missing in one place.
const PAGE_RULES: [prefix: string, oneOf: PermissionCode[]][] = [
  ['players', PagePermissions.players],
  ['items/bundle', PagePermissions.catalogBundles],
  ['items', PagePermissions.catalogItems],
  ['season-passes', PagePermissions.seasonPasses],
  ['gifts', PagePermissions.gifts],
  ['translations', PagePermissions.translations],
  ['transactions/inventory', PagePermissions.transactionsInventory],
  ['transactions', PagePermissions.transactionsStore],
  ['store', PagePermissions.snapshots],
  ['settings/import', PagePermissions.settingsImport],
  ['settings', PagePermissions.settingsGeneral],
];

/**
 * What the page at `pathUnderSandbox` ('gifts', 'settings/import', …) requires, or
 * undefined when nothing gates it. For deciding whether a URL survives a Sandbox
 * switch: the Grants that open a page in one Sandbox may not open it in another.
 */
export const pagePermissionsFor = (pathUnderSandbox: string) =>
  PAGE_RULES.find(
    ([prefix]) =>
      pathUnderSandbox === prefix || pathUnderSandbox.startsWith(`${prefix}/`),
  )?.[1];
